import * as THREE from 'three';
import { useViewFeaturesStore, PaintedTerrainArea, TerrainFeatureType } from '@/store/viewFeaturesStore';
import { useThreeSceneStore } from '@/store/threeSceneStore';
import { formaStorageService } from '@/services/forma/storage-service';

export const LANDMARK_COLOR = 0xFFD700; // Gold for landmarks
const FEATURE_COLORS: Record<TerrainFeatureType, number> = {
	greenery: 0x00ff00,
	water: 0x0066ff,
	park: 0x66bb6a,
	none: 0xab8845,
};

/**
 * Mark a building as a landmark
 */
export const markBuildingAsLandmark = (building: THREE.Object3D, scene: THREE.Scene): void => {
	const { addLandmark, isLandmark } = useViewFeaturesStore.getState();
	
	if (isLandmark(building.uuid)) {
		return;
	}
	
	// Store original material
	let originalMaterial: THREE.Material | THREE.Material[] | null = null;
	
	building.traverse((child) => {
		if (child instanceof THREE.Mesh) {
			if (!originalMaterial) {
				originalMaterial = Array.isArray(child.material) 
					? child.material.map(m => m.clone())
					: child.material.clone();
			}
			
			// Apply landmark material to change building color
			const landmarkMaterial = new THREE.MeshPhysicalMaterial({
				color: LANDMARK_COLOR,
				metalness: 0.2,
				roughness: 0.3,
				reflectivity: 0.7,
				clearcoat: 0.3,
				clearcoatRoughness: 0.2,
				emissive: LANDMARK_COLOR,
				emissiveIntensity: 0.2,
			});
			
			child.material = landmarkMaterial;
			child.userData.isLandmark = true;
		}
	});
	
	if (originalMaterial) {
		addLandmark(building.uuid, building.name || 'Unnamed Building', originalMaterial);
		
		// Add star sprite above the building
		addLandmarkGlow(building, scene);
	}
};

/**
 * Remove landmark status from a building
 */
export const removeLandmarkFromBuilding = (building: THREE.Object3D, scene: THREE.Scene): void => {
	const { landmarks, removeLandmark } = useViewFeaturesStore.getState();
	const landmark = landmarks.get(building.uuid);
	
	if (!landmark) {
		return;
	}
	
	// Restore original material
	let materialIndex = 0;
	building.traverse((child) => {
		if (child instanceof THREE.Mesh) {
			if (Array.isArray(landmark.originalMaterial)) {
				child.material = landmark.originalMaterial[materialIndex++];
			} else {
				child.material = landmark.originalMaterial;
			}
			child.userData.isLandmark = false;
		}
	});
	
	removeLandmark(building.uuid);
	removeLandmarkGlow(building, scene);
};

/**
 * Create a star texture for sprite
 */
const createStarTexture = (): THREE.Texture => {
	const canvas = document.createElement('canvas');
	const size = 256;
	canvas.width = size;
	canvas.height = size;
	
	const ctx = canvas.getContext('2d');
	if (!ctx) throw new Error('Could not get canvas context');
	
	// Draw a 5-pointed star
	ctx.fillStyle = '#FFD700'; // Gold color
	ctx.beginPath();
	
	const centerX = size / 2;
	const centerY = size / 2;
	const outerRadius = size * 0.4;
	const innerRadius = size * 0.16;
	const points = 5;
	
	for (let i = 0; i < points * 2; i++) {
		const radius = i % 2 === 0 ? outerRadius : innerRadius;
		const angle = (i * Math.PI) / points - Math.PI / 2;
		const x = centerX + Math.cos(angle) * radius;
		const y = centerY + Math.sin(angle) * radius;
		
		if (i === 0) {
			ctx.moveTo(x, y);
		} else {
			ctx.lineTo(x, y);
		}
	}
	
	ctx.closePath();
	ctx.fill();
	
	// Add glow effect
	ctx.shadowColor = '#FFD700';
	ctx.shadowBlur = 20;
	ctx.fill();
	
	const texture = new THREE.CanvasTexture(canvas);
	texture.needsUpdate = true;
	return texture;
};

/**
 * Add star sprite above landmark building
 */
const addLandmarkGlow = (building: THREE.Object3D, scene: THREE.Scene): void => {
	const bbox = new THREE.Box3().setFromObject(building);
	const size = new THREE.Vector3();
	bbox.getSize(size);
	const center = new THREE.Vector3();
	bbox.getCenter(center);
	
	// Create star sprite above the building
	const starSize = Math.min(Math.max(size.x, size.y) * 0.3, 5); // Scale with building but cap at 5 units
	const starTexture = createStarTexture();
	
	const spriteMaterial = new THREE.SpriteMaterial({
		map: starTexture,
		color: 0xffffff,
		transparent: true,
		opacity: 1.0,
		depthWrite: false,
		sizeAttenuation: true,
	});
	
	const sprite = new THREE.Sprite(spriteMaterial);
	sprite.scale.set(starSize, starSize, 1);
	sprite.position.set(center.x, center.y, bbox.max.z + starSize * 0.7); // Position above building
	sprite.name = `__landmarkGlow_${building.uuid}`;
	sprite.userData.isLandmarkGlow = true;
	sprite.renderOrder = 1000; // Render on top
	
	scene.add(sprite);
};

/**
 * Remove star indicator from landmark building
 */
const removeLandmarkGlow = (building: THREE.Object3D, scene: THREE.Scene): void => {
	const glowName = `__landmarkGlow_${building.uuid}`;
	const glow = scene.getObjectByName(glowName);
	
	if (glow) {
		scene.remove(glow);
		if (glow instanceof THREE.Sprite) {
			// Dispose sprite material and texture
			if (glow.material instanceof THREE.SpriteMaterial) {
				if (glow.material.map) {
					glow.material.map.dispose();
				}
				glow.material.dispose();
			}
		} else if (glow instanceof THREE.Mesh) {
			glow.geometry.dispose();
			if (glow.material instanceof THREE.Material) {
				glow.material.dispose();
			}
		}
	}
};

/**
 * Paint a terrain area with a feature type
 */
export const paintTerrainArea = (
	points: THREE.Vector3[],
	featureType: TerrainFeatureType,
	scene: THREE.Scene
): void => {
	if (points.length < 3) {
		console.warn('Need at least 3 points to paint an area');
		return;
	}
	
	const { addPaintedArea } = useViewFeaturesStore.getState();
	
	// Create painted area object
	const area: PaintedTerrainArea = {
		id: `painted_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
		type: featureType,
		points: points.map(p => p.clone()),
		color: FEATURE_COLORS[featureType],
	};
	
	addPaintedArea(area);
	
	// Visualize the painted area
	visualizePaintedArea(area, scene);
};

/**
 * Helper function to check if a 2D point is inside a polygon using ray casting
 */
const isPointInPolygon2D = (point: { x: number; y: number }, polygon: THREE.Vector3[]): boolean => {
	let inside = false;
	for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
		const xi = polygon[i].x, yi = polygon[i].y;
		const xj = polygon[j].x, yj = polygon[j].y;
		
		const intersect = ((yi > point.y) !== (yj > point.y))
			&& (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
		if (intersect) inside = !inside;
	}
	return inside;
};

/**
 * Visualize a painted terrain area in the scene
 */
export const visualizePaintedArea = (area: PaintedTerrainArea, scene: THREE.Scene): void => {
	if (area.points.length < 3) return;
	
	// Get terrain objects for raycasting
	const terrainObjects: THREE.Object3D[] = [];
	scene.traverse((child: THREE.Object3D) => {
		if (child.userData.category === 'terrain') {
			terrainObjects.push(child);
		}
	});
	
	if (terrainObjects.length === 0) {
		console.warn('No terrain found for painted area projection');
		return;
	}
	
	// Calculate bounding box
	const bounds = {
		minX: Math.min(...area.points.map(p => p.x)),
		maxX: Math.max(...area.points.map(p => p.x)),
		minY: Math.min(...area.points.map(p => p.y)),
		maxY: Math.max(...area.points.map(p => p.y)),
	};
	
	// Grid resolution - smaller cells for better fill coverage
	const gridResolution = 0.5;
	
	// Create a 2D grid of sampled terrain heights
	const gridPoints = new Map<string, THREE.Vector3>(); // key: "x,y"
	const raycaster = new THREE.Raycaster();
	const avgZ = area.points.reduce((sum, p) => sum + p.z, 0) / area.points.length;
	const maxZ = Math.max(...area.points.map(p => p.z)) + 100;
	
	// Sample points in a grid
	for (let x = bounds.minX; x <= bounds.maxX; x += gridResolution) {
		for (let y = bounds.minY; y <= bounds.maxY; y += gridResolution) {
			// Check if point is inside polygon
			if (isPointInPolygon2D({ x, y }, area.points)) {
				// Raycast to find terrain height
				const rayOrigin = new THREE.Vector3(x, y, maxZ);
				const rayDirection = new THREE.Vector3(0, 0, -1);
				raycaster.set(rayOrigin, rayDirection);
				
				const intersects = raycaster.intersectObjects(terrainObjects, true);
				const z = intersects.length > 0 ? intersects[0].point.z : avgZ;
				
				const key = `${Math.round(x / gridResolution)},${Math.round(y / gridResolution)}`;
				gridPoints.set(key, new THREE.Vector3(x, y, z + 0.1));
			}
		}
	}
	
	// Create mesh from grid - connect adjacent points into quads (2 triangles each)
	const geometry = new THREE.BufferGeometry();
	const vertices: number[] = [];
	
	for (let x = bounds.minX; x < bounds.maxX; x += gridResolution) {
		for (let y = bounds.minY; y < bounds.maxY; y += gridResolution) {
			const kx = Math.round(x / gridResolution);
			const ky = Math.round(y / gridResolution);
			
			const p1 = gridPoints.get(`${kx},${ky}`);
			const p2 = gridPoints.get(`${kx + 1},${ky}`);
			const p3 = gridPoints.get(`${kx + 1},${ky + 1}`);
			const p4 = gridPoints.get(`${kx},${ky + 1}`);
			
			// Create 2 triangles if all 4 corners exist
			if (p1 && p2 && p3 && p4) {
				// Triangle 1: p1, p2, p3
				vertices.push(p1.x, p1.y, p1.z);
				vertices.push(p2.x, p2.y, p2.z);
				vertices.push(p3.x, p3.y, p3.z);
				
				// Triangle 2: p1, p3, p4
				vertices.push(p1.x, p1.y, p1.z);
				vertices.push(p3.x, p3.y, p3.z);
				vertices.push(p4.x, p4.y, p4.z);
			} else if (p1 && p2 && p3) {
				// Only triangle 1
				vertices.push(p1.x, p1.y, p1.z);
				vertices.push(p2.x, p2.y, p2.z);
				vertices.push(p3.x, p3.y, p3.z);
			} else if (p1 && p3 && p4) {
				// Only triangle 2
				vertices.push(p1.x, p1.y, p1.z);
				vertices.push(p3.x, p3.y, p3.z);
				vertices.push(p4.x, p4.y, p4.z);
			}
		}
	}
	
	if (vertices.length === 0) {
		console.warn('No triangles created for painted area');
		return;
	}
	
	geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
	geometry.computeVertexNormals();
	geometry.computeBoundingSphere();
	
	const material = new THREE.MeshBasicMaterial({
		color: area.color,
		transparent: true,
		opacity: 0.5,
		side: THREE.DoubleSide,
		depthWrite: false,
	});
	
	const mesh = new THREE.Mesh(geometry, material);
	mesh.name = `__paintedArea_${area.id}`;
	mesh.userData.isPaintedArea = true;
	mesh.userData.featureType = area.type;
	mesh.userData.paintedAreaId = area.id;
	mesh.renderOrder = 2;
	
	scene.add(mesh);
	
	// Add outline that follows terrain contours
	const outlinePoints = [...area.points, area.points[0]].map(p => 
		new THREE.Vector3(p.x, p.y, p.z + 0.12)
	);
	const lineGeometry = new THREE.BufferGeometry().setFromPoints(outlinePoints);
	const lineMaterial = new THREE.LineBasicMaterial({
		color: area.color,
		linewidth: 2,
		transparent: true,
		opacity: 0.8,
	});
	const line = new THREE.Line(lineGeometry, lineMaterial);
	line.name = `__paintedAreaOutline_${area.id}`;
	line.userData.isPaintedAreaOutline = true;
	
	scene.add(line);
};

/**
 * Remove painted area visualization from scene
 */
export const removePaintedAreaVisualization = (areaId: string, scene: THREE.Scene): void => {
	const meshName = `__paintedArea_${areaId}`;
	const outlineName = `__paintedAreaOutline_${areaId}`;
	
	const mesh = scene.getObjectByName(meshName);
	const outline = scene.getObjectByName(outlineName);
	
	if (mesh) {
		scene.remove(mesh);
		if (mesh instanceof THREE.Mesh) {
			mesh.geometry.dispose();
			if (mesh.material instanceof THREE.Material) {
				mesh.material.dispose();
			}
		}
	}
	
	if (outline) {
		scene.remove(outline);
		if (outline instanceof THREE.Line) {
			outline.geometry.dispose();
			if (outline.material instanceof THREE.Material) {
				outline.material.dispose();
			}
		}
	}
};

/**
 * Check if a building is a landmark
 */
export const isBuildingLandmark = (buildingUuid: string): boolean => {
	const { isLandmark } = useViewFeaturesStore.getState();
	return isLandmark(buildingUuid);
};

// ============================================================================
// PERSISTENCE - Save and Load to Forma Storage
// ============================================================================

const STORAGE_KEY_LANDMARKS = 'viewFeatures:landmarks';
const STORAGE_KEY_PAINTED_AREAS = 'viewFeatures:paintedAreas';

/**
 * Serializable types for storage
 */
interface SerializedPaintedArea {
	id: string;
	type: TerrainFeatureType;
	points: { x: number; y: number; z: number }[];
	color: number;
}

interface SerializedLandmark {
	uuid: string;
	name: string;
	path: string; // Forma building path for stable identification
}

/**
 * Save landmarks to Forma storage
 */
export const saveLandmarksToStorage = async (scene?: THREE.Scene): Promise<void> => {
	const { setAreEventsPaused } = useThreeSceneStore.getState();
	
	try {
		// Pause events to prevent auto-save re-triggering during save
		setAreEventsPaused(true);
		
		const { landmarks } = useViewFeaturesStore.getState();
		
		// Convert Map to serializable array with building paths
		const serializedLandmarks: SerializedLandmark[] = [];
		
		for (const landmark of landmarks.values()) {
			// Find building in scene to get its path
			let buildingPath = '';
			if (scene) {
				const building = scene.getObjectByProperty('uuid', landmark.uuid);
				if (building) {
					buildingPath = building.userData.path || '';
				} else {
					console.warn('Building not found in scene for landmark:', landmark);
				}
			}
			
			serializedLandmarks.push({
				uuid: landmark.uuid,
				name: landmark.name,
				path: buildingPath,
			});
		}
		
		await formaStorageService.setJSON(STORAGE_KEY_LANDMARKS, serializedLandmarks);
	} catch (error) {
		console.error('Error saving landmarks to storage:', error);
	} finally {
		// Always resume events
		setAreEventsPaused(false);
	}
};

/**
 * Save painted areas to Forma storage
 */
export const savePaintedAreasToStorage = async (): Promise<void> => {
	const { setAreEventsPaused } = useThreeSceneStore.getState();
	
	try {
		// Pause events to prevent auto-save re-triggering during save
		setAreEventsPaused(true);
		
		const { paintedAreas } = useViewFeaturesStore.getState();
		
		// Convert Vector3 points to serializable objects
		const serializedAreas: SerializedPaintedArea[] = paintedAreas.map(area => ({
			id: area.id,
			type: area.type,
			color: area.color,
			points: area.points.map(p => ({ x: p.x, y: p.y, z: p.z })),
		}));
		
		await formaStorageService.setJSON(STORAGE_KEY_PAINTED_AREAS, serializedAreas);
	} catch (error) {
		console.error('Error saving painted areas to storage:', error);
	} finally {
		// Always resume events
		setAreEventsPaused(false);
	}
};

/**
 * Clear all landmark visual elements from scene
 */
const clearLandmarkVisualsFromScene = (scene: THREE.Scene): void => {
	// Remove all landmark stars
	const starsToRemove: THREE.Object3D[] = [];
	scene.traverse((obj) => {
		if (obj.userData.isLandmarkGlow) {
			starsToRemove.push(obj);
		}
	});
	starsToRemove.forEach(star => {
		if (star.parent) {
			star.parent.remove(star);
		}
		// Dispose sprite material and texture
		if (star instanceof THREE.Sprite && star.material instanceof THREE.SpriteMaterial) {
			if (star.material.map) {
				star.material.map.dispose();
			}
			star.material.dispose();
		}
	});
};

/**
 * Clear all painted area visual elements from scene
 */
const clearPaintedAreaVisualsFromScene = (scene: THREE.Scene): void => {
	const objectsToRemove: THREE.Object3D[] = [];
	scene.traverse((obj) => {
		if (obj.userData.isPaintedArea || obj.userData.isPaintedAreaOutline) {
			objectsToRemove.push(obj);
		}
	});
	objectsToRemove.forEach(obj => {
		scene.remove(obj);
		// Dispose geometry and material
		if (obj instanceof THREE.Mesh || obj instanceof THREE.Line) {
			obj.geometry.dispose();
			if (obj.material instanceof THREE.Material) {
				obj.material.dispose();
			}
		}
	});
};

/**
 * Load landmarks from Forma storage and re-apply them to scene
 * Note: Events should be paused by caller to prevent auto-save during load
 */
export const loadLandmarksFromStorage = async (scene: THREE.Scene): Promise<void> => {
	try {
		// Clear existing landmarks from store and scene to prevent duplicates
		const { clearLandmarks } = useViewFeaturesStore.getState();
		clearLandmarks();
		clearLandmarkVisualsFromScene(scene);
		
		const serializedLandmarks = await formaStorageService.getJSON<SerializedLandmark[]>(STORAGE_KEY_LANDMARKS);
		
		if (!serializedLandmarks || serializedLandmarks.length === 0) {
			return;
		}
		
		// Find buildings in scene and re-apply landmark status
		for (const landmark of serializedLandmarks) {
			let building: THREE.Object3D | undefined = undefined;
			
			// Strategy 1: Try to find by path first (userData.path - most stable)
			if (landmark.path) {
				scene.traverse((obj) => {
					if (obj.userData.path === landmark.path && obj.userData.category === 'building') {
						building = obj;
					}
				});
			}
			
			// Strategy 2: Try to find by name (object.name - fallback)
			if (!building && landmark.name) {
				building = scene.getObjectByName(landmark.name);
				// Verify it's actually a building
				if (building && building.userData.category !== 'building') {
					building = undefined;
				}
			}
			
			// Strategy 3: Fallback to UUID (least stable, changes between reloads)
			if (!building && landmark.uuid) {
				building = scene.getObjectByProperty('uuid', landmark.uuid);
				// Verify it's actually a building
				if (building && building.userData.category !== 'building') {
					building = undefined;
				}
			}
			
			if (building) {
				markBuildingAsLandmark(building, scene);
			} else {
				console.warn('Could not find building for landmark:', landmark);
			}
		}
	} catch (error) {
		console.error('Error loading landmarks from storage:', error);
	}
};

/**
 * Load painted areas from Forma storage and re-visualize them
 * Note: Events should be paused by caller to prevent auto-save during load
 */
export const loadPaintedAreasFromStorage = async (scene: THREE.Scene): Promise<void> => {
	try {
		// Clear existing painted areas from store and scene to prevent duplicates
		const { clearPaintedAreas } = useViewFeaturesStore.getState();
		clearPaintedAreas();
		clearPaintedAreaVisualsFromScene(scene);
		
		const serializedAreas = await formaStorageService.getJSON<SerializedPaintedArea[]>(STORAGE_KEY_PAINTED_AREAS);
		
		if (!serializedAreas || serializedAreas.length === 0) {
			return;
		}
				
		const { addPaintedArea } = useViewFeaturesStore.getState();
		
		// Convert back to PaintedTerrainArea with Vector3 points
		for (const serializedArea of serializedAreas) {
			const area: PaintedTerrainArea = {
				id: serializedArea.id,
				type: serializedArea.type,
				color: serializedArea.color,
				points: serializedArea.points.map(p => new THREE.Vector3(p.x, p.y, p.z)),
			};
			
			// Add to store and visualize
			addPaintedArea(area);
			visualizePaintedArea(area, scene);
		}
	} catch (error) {
		console.error('Error loading painted areas from storage:', error);
	}
};

/**
 * Load all view features from storage (landmarks and painted areas)
 */
export const loadViewFeaturesFromStorage = async (scene: THREE.Scene): Promise<void> => {
	const { setAreEventsPaused } = useThreeSceneStore.getState();
	
	try {
		// Pause events once for both operations to prevent race condition
		setAreEventsPaused(true);
		
		await Promise.all([
			loadLandmarksFromStorage(scene),
			loadPaintedAreasFromStorage(scene),
		]);
	} catch (error) {
		console.error('Error loading view features from storage:', error);
	} finally {
		// Always resume events after both operations complete
		setAreEventsPaused(false);
	}
};

/**
 * Clear all landmarks from scene and store
 * Restores original materials and removes visual indicators
 */
export const clearAllLandmarks = (scene: THREE.Scene): void => {
	const { setAreEventsPaused } = useThreeSceneStore.getState();
	
	try {
		// Pause events to prevent auto-save during clear
		setAreEventsPaused(true);
		
		const { landmarks, clearLandmarks } = useViewFeaturesStore.getState();
		
		// For each landmark, restore original material
		landmarks.forEach((landmark, uuid) => {
			const building = scene.getObjectByProperty('uuid', uuid);
			if (building) {
				// Restore original material
				let materialIndex = 0;
				building.traverse((child) => {
					if (child instanceof THREE.Mesh) {
						if (Array.isArray(landmark.originalMaterial)) {
							child.material = landmark.originalMaterial[materialIndex++];
						} else {
							child.material = landmark.originalMaterial;
						}
						child.userData.isLandmark = false;
					}
				});
			}
		});
		
		// Remove all star sprites
		clearLandmarkVisualsFromScene(scene);
		
		// Clear store
		clearLandmarks();
	} finally {
		// Always resume events
		setAreEventsPaused(false);
	}
};

/**
 * Clear all painted areas from scene and store
 */
export const clearAllPaintedAreas = (scene: THREE.Scene): void => {
	const { setAreEventsPaused } = useThreeSceneStore.getState();
	
	try {
		// Pause events to prevent auto-save during clear
		setAreEventsPaused(true);
		
		const { clearPaintedAreas } = useViewFeaturesStore.getState();
		
		// Remove all painted area meshes
		clearPaintedAreaVisualsFromScene(scene);
		
		// Clear store
		clearPaintedAreas();
	} finally {
		// Always resume events
		setAreEventsPaused(false);
	}
};

/**
 * Create a canvas texture for terrain feature tooltip
 */
const createTerrainFeatureTooltipTexture = (featureType: TerrainFeatureType, area: string): THREE.CanvasTexture => {
	const canvas = document.createElement('canvas');
	const context = canvas.getContext('2d')!;
	canvas.width = 512;
	canvas.height = 256;
	
	context.clearRect(0, 0, canvas.width, canvas.height);
	
	const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
	gradient.addColorStop(0, 'rgba(0, 0, 0, 0.95)');
	gradient.addColorStop(1, 'rgba(20, 40, 20, 0.95)');
	context.fillStyle = gradient;
	
	const radius = 20;
	context.beginPath();
	context.moveTo(radius, 0);
	context.lineTo(canvas.width - radius, 0);
	context.quadraticCurveTo(canvas.width, 0, canvas.width, radius);
	context.lineTo(canvas.width, canvas.height - radius);
	context.quadraticCurveTo(canvas.width, canvas.height, canvas.width - radius, canvas.height);
	context.lineTo(radius, canvas.height);
	context.quadraticCurveTo(0, canvas.height, 0, canvas.height - radius);
	context.lineTo(0, radius);
	context.quadraticCurveTo(0, 0, radius, 0);
	context.closePath();
	context.fill();
	
	context.strokeStyle = 'rgba(152, 251, 152, 0.3)';
	context.lineWidth = 4;
	context.stroke();
	
	const featureNames: Record<string, string> = {
		water: 'Water',
		greenery: 'Greenery',
		park: 'Park',
		none: 'None',
	};
	
	const displayName = featureNames[featureType] || featureType;
	
	// Draw feature type
	context.font = 'Bold 48px Arial';
	context.fillStyle = '#98FB98';
	context.textBaseline = 'top';
	context.fillText(displayName, 30, 40);
	
	// Draw "Area:" label and value - centered vertically
	context.textBaseline = 'middle';
	const centerY = canvas.height / 2 + 30; // Slightly below center to balance with title
	
	context.font = '36px Arial';
	context.fillStyle = '#B0B0B0';
	context.fillText('Area:', 30, centerY);
	
	// Draw area value
	context.font = 'Bold 42px Arial';
	context.fillStyle = 'white';
	context.fillText(area, 130, centerY);
	
	const texture = new THREE.CanvasTexture(canvas);
	texture.needsUpdate = true;
	return texture;
};

/**
 * Show terrain feature tooltip sprite in 3D scene
 */
export const showTerrainFeatureTooltip = (
	scene: THREE.Scene,
	camera: THREE.Camera,
	position: THREE.Vector3,
	featureType: TerrainFeatureType,
	area: string
): void => {
	removeTerrainFeatureTooltip(scene);
	
	const texture = createTerrainFeatureTooltipTexture(featureType, area);
	
	const spriteMaterial = new THREE.SpriteMaterial({
		map: texture,
		transparent: true,
		opacity: 1.0,
		depthTest: false,
		depthWrite: false,
		sizeAttenuation: true,
	});
	
	const sprite = new THREE.Sprite(spriteMaterial);
	
	// Calculate camera distance for proper scaling
	const distanceToCamera = camera.position.distanceTo(position);
	const scale = distanceToCamera * 0.1; // Adjust multiplier for desired size
	sprite.scale.set(scale * 2, scale, 1); // 2:1 aspect ratio
	
	sprite.position.copy(position);
	
	sprite.name = '__terrainFeatureTooltip';
	sprite.userData.isTerrainFeatureTooltip = true;
	sprite.renderOrder = 2000;
	
	scene.add(sprite);
};

/**
 * Remove terrain feature tooltip sprite from scene
 */
export const removeTerrainFeatureTooltip = (scene: THREE.Scene): void => {
	const tooltip = scene.getObjectByName('__terrainFeatureTooltip');
	
	if (tooltip) {
		scene.remove(tooltip);
		if (tooltip instanceof THREE.Sprite && tooltip.material instanceof THREE.SpriteMaterial) {
			if (tooltip.material.map) {
				tooltip.material.map.dispose();
			}
			tooltip.material.dispose();
		}
	}
};

/**
 * Show edit handles for a painted area
 */
export const showPaintedAreaHandles = (areaId: string, scene: THREE.Scene): void => {
	const { paintedAreas } = useViewFeaturesStore.getState();
	const area = paintedAreas.find(a => a.id === areaId);
	
	if (!area) return;
	
	removePaintedAreaHandles(scene);
	
	const handlesGroup = new THREE.Group();
	handlesGroup.name = '__paintedAreaHandles';
	handlesGroup.userData.isPaintedAreaHandles = true;
	handlesGroup.userData.areaId = areaId;
	
	area.points.forEach((point, index) => {
		// Create main handle sphere
		const handleGeometry = new THREE.SphereGeometry(0.5, 16, 16);
		const handleMaterial = new THREE.MeshBasicMaterial({
			color: 0xffffff,
			transparent: true,
			opacity: 1.0,
			depthTest: false,
		});
		
		const handle = new THREE.Mesh(handleGeometry, handleMaterial);
		handle.position.copy(point);
		handle.position.z += 0.5; // Slightly above terrain
		handle.name = `__handle_${index}`;
		handle.userData.isHandle = true;
		handle.userData.handleIndex = index;
		handle.userData.areaId = areaId;
		handle.renderOrder = 2001; // Above everything else
		
		// Create outer glow ring
		const glowGeometry = new THREE.SphereGeometry(0.7, 16, 16);
		const glowMaterial = new THREE.MeshBasicMaterial({
			color: area.color,
			transparent: true,
			opacity: 0.4,
			depthTest: false,
		});
		
		const glow = new THREE.Mesh(glowGeometry, glowMaterial);
		glow.position.copy(point);
		glow.position.z += 0.5;
		glow.name = `__handleGlow_${index}`;
		glow.userData.isHandleGlow = true;
		glow.userData.handleIndex = index;
		glow.userData.areaId = areaId;
		glow.renderOrder = 2000; // Behind handle but above everything else
		
		handlesGroup.add(glow);
		handlesGroup.add(handle);
	});
	
	scene.add(handlesGroup);
};

/**
 * Remove edit handles from scene
 */
export const removePaintedAreaHandles = (scene: THREE.Scene): void => {
	const handles = scene.getObjectByName('__paintedAreaHandles');
	
	if (handles) {
		handles.traverse((child) => {
			if (child instanceof THREE.Mesh) {
				child.geometry.dispose();
				if (child.material instanceof THREE.Material) {
					child.material.dispose();
				}
			}
		});
		scene.remove(handles);
	}
};

/**
 * Subdivide polygon edges that are too long by adding intermediate points
 */
const subdividePolygonEdges = (
	points: THREE.Vector3[],
	draggedIndex: number,
	maxEdgeLength: number = 10.0
): { points: THREE.Vector3[], newDraggedIndex: number } => {
	const newPoints: THREE.Vector3[] = [];
	let newDraggedIndex = draggedIndex;
	
	for (let i = 0; i < points.length; i++) {
		const currentPoint = points[i];
		const nextPoint = points[(i + 1) % points.length];
		
		newPoints.push(currentPoint.clone());
		
		const dx = nextPoint.x - currentPoint.x;
		const dy = nextPoint.y - currentPoint.y;
		const edgeLength = Math.sqrt(dx * dx + dy * dy);
		
		// If edge is too long, add intermediate points
		if (edgeLength > maxEdgeLength) {
			const numSubdivisions = Math.ceil(edgeLength / maxEdgeLength);
			
			for (let j = 1; j < numSubdivisions; j++) {
				const t = j / numSubdivisions;
				const intermediatePoint = new THREE.Vector3(
					currentPoint.x + dx * t,
					currentPoint.y + dy * t,
					currentPoint.z + (nextPoint.z - currentPoint.z) * t
				);
				newPoints.push(intermediatePoint);
				
				// Adjust dragged index if we're adding points before it
				if (i < draggedIndex) {
					newDraggedIndex++;
				}
			}
		}
	}
	
	return { points: newPoints, newDraggedIndex };
};

/**
 * Remove a handle point from a painted area
 */
export const removeHandlePoint = (
	areaId: string,
	handleIndex: number,
	scene: THREE.Scene
): boolean => {
	const { updatePaintedAreaPoints, paintedAreas } = useViewFeaturesStore.getState();
	const area = paintedAreas.find(a => a.id === areaId);
	
	if (!area) return false;
	
	if (area.points.length <= 3) {
		console.warn('Cannot remove point: polygon must have at least 3 points');
		return false;
	}
	
	// Remove the point at the specified index
	const newPoints = area.points.filter((_, index) => index !== handleIndex);
	
	// Update store
	updatePaintedAreaPoints(areaId, newPoints);
	
	// Get updated area from store
	const updatedState = useViewFeaturesStore.getState();
	const updatedArea = updatedState.paintedAreas.find(a => a.id === areaId);
	
	if (updatedArea) {
		// Re-visualize with updated points
		removePaintedAreaVisualization(areaId, scene);
		visualizePaintedArea(updatedArea, scene);
		
		// Regenerate handles
		removePaintedAreaHandles(scene);
		showPaintedAreaHandles(areaId, scene);
	}
	
	return true;
};

/**
 * Update handle position
 */
export const updateHandlePosition = (
	areaId: string,
	handleIndex: number,
	newPosition: THREE.Vector3,
	scene: THREE.Scene,
	saveToStore: boolean = true
): THREE.Vector3[] | null => {
	const { updatePaintedAreaPoints, paintedAreas } = useViewFeaturesStore.getState();
	const area = paintedAreas.find(a => a.id === areaId);
	
	if (!area) return null;
	
	let newPoints = [...area.points];
	newPoints[handleIndex] = newPosition.clone();
	
	removePaintedAreaVisualization(areaId, scene);
	visualizePaintedArea({ ...area, points: newPoints }, scene);
	
	if (saveToStore) {
		const { points: subdividedPoints } = subdividePolygonEdges(newPoints, handleIndex);
		newPoints = subdividedPoints;
		
		updatePaintedAreaPoints(areaId, newPoints);
		
		const updatedState = useViewFeaturesStore.getState();
		const updatedArea = updatedState.paintedAreas.find(a => a.id === areaId);
		
		if (updatedArea) {
			removePaintedAreaVisualization(areaId, scene);
			visualizePaintedArea(updatedArea, scene);
			
			removePaintedAreaHandles(scene);
			showPaintedAreaHandles(areaId, scene);
		}
	} else {
		const handlesGroup = scene.getObjectByName('__paintedAreaHandles');
		if (handlesGroup) {
			const handle = handlesGroup.getObjectByName(`__handle_${handleIndex}`);
			const glow = handlesGroup.getObjectByName(`__handleGlow_${handleIndex}`);
			
			if (handle) {
				handle.position.copy(newPosition);
				handle.position.z += 0.5;
			}
			
			if (glow) {
				glow.position.copy(newPosition);
				glow.position.z += 0.5;
			}
		}
	}
	
	return newPoints;
};
